if (!customElements.get('mc-hero-carousel')) {
class MCHeroCarousel extends HTMLElement {
  #abortController = null;
  #activeIndex = 0;
  #requestedIndex = 0;
  #animationFrame = 0;
  #settleTimer = 0;
  #autoplayTimer = 0;
  #programmaticTimer = 0;
  #resizeObserver = null;
  #motionQuery = null;
  #programmatic = false;
  #userStopped = false;
  #hovered = false;
  #playIntent = null;
  #items = [];
  #loop = false;
  #requestedPhysical = 0;
  #supportsScrollEnd = false;
  #userInteracted = false;
  #dots = [];
  #counter = null;

  connectedCallback() {
    if (this.#abortController) return;

    this.track = this.querySelector('[data-mc-hero-track]');
    this.#applySchedule();
    this.slides = [...this.querySelectorAll('[data-mc-hero-slide]')];
    this.controls = this.querySelector('[data-mc-hero-controls]');
    this.previousButton = this.querySelector('[data-mc-hero-previous]');
    this.nextButton = this.querySelector('[data-mc-hero-next]');
    this.playButton = this.querySelector('[data-mc-hero-play]');
    this.status = this.querySelector('[data-mc-hero-status]');
    this.pagination = this.querySelector('[data-mc-hero-pagination]');

    if (!this.track || this.slides.length === 0) return;

    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#userStopped = false;
    this.#programmatic = false;
    this.#userInteracted = false;
    this.#activeIndex = 0;
    this.#requestedIndex = 0;
    this.#hovered = this.matches(':hover');
    this.#playIntent = null;
    this.#items = this.slides;
    this.#supportsScrollEnd = 'onscrollend' in this.track;

    this.previousButton?.addEventListener('click', () => this.#moveBy(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.#moveBy(1), { signal });
    this.playButton?.addEventListener('click', () => this.#toggleAutoplay(), { signal });
    this.playButton?.addEventListener('pointerdown', () => { this.#playIntent = this.#userStopped; }, { passive: true, signal });
    this.playButton?.addEventListener('pointercancel', () => { this.#playIntent = null; }, { signal });
    this.track.addEventListener('scroll', () => this.#onScroll(), { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.#settle(true), { signal });
    this.track.addEventListener('keydown', (event) => this.#onKeydown(event), { signal });
    this.track.addEventListener('click', (event) => this.#onTrackClick(event), { signal });
    this.track.addEventListener('pointerdown', () => this.#onManualInput(), { passive: true, signal });
    this.track.addEventListener('wheel', () => this.#onManualInput(), { passive: true, signal });
    this.addEventListener('focusin', () => this.#stopAutoplay(true), { signal });
    this.addEventListener('mouseenter', () => this.#setHovered(true), { signal });
    this.addEventListener('mouseleave', () => this.#setHovered(false), { signal });
    this.addEventListener('shopify:block:select', (event) => this.#onBlockSelect(event), { signal });
    document.addEventListener('visibilitychange', () => this.#onVisibilityChange(), { signal });
    this.#motionQuery.addEventListener?.('change', () => this.#onMotionChange(), { signal });

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        this.#onResize();
      });
      // Border box: the pre-enhancement centering padding is removed during setup and must not re-trigger.
      this.#resizeObserver.observe(this.track, { box: 'border-box' });
    }

    this.classList.add('is-enhanced');
    this.classList.toggle('has-multiple', this.slides.length > 1);
    if (this.slides.length < 2) this.classList.remove('mc-hero-carousel--multi');
    this.#buildPagination(signal);
    this.#setupLoop();
    if (this.controls && this.slides.length > 1) this.controls.hidden = false;
    this.#applyState(this.#nearestIndex(), false);
    this.#syncPlayButton();
    this.#scheduleAutoplay();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#motionQuery = null;
    this.#clearTimers();
    for (const item of this.#items) {
      if (item.hasAttribute('data-mc-hero-copy')) item.remove();
    }
    this.#items = [];
    this.#loop = false;
    this.#dots = [];
    this.#counter = null;
    this.pagination?.replaceChildren();
    this.classList.remove('is-looping');
    this.classList.remove('is-enhanced');
    this.classList.remove('has-multiple');
    if (this.controls) this.controls.hidden = true;
    if (this.status) this.status.textContent = '';
    for (const slide of this.slides || []) {
      slide.removeAttribute('aria-hidden');
      slide.inert = false;
    }
  }

  // Storefront pages can be served from cache, so a slide may arrive after its end time (or before its start).
  #applySchedule() {
    if (!this.track || this.dataset.editor === 'true') return;
    const now = Date.now() / 1000;
    const all = [...this.track.querySelectorAll('[data-mc-hero-slide]')];
    const outside = all.filter((slide) => {
      const start = Number(slide.dataset.mcStart) || 0;
      const end = Number(slide.dataset.mcEnd) || 0;
      return (start > 0 && now < start) || (end > 0 && now >= end);
    });
    if (outside.length === 0) return;

    for (const slide of outside) slide.remove();
    const remaining = all.filter((slide) => !outside.includes(slide));
    if (remaining.length === 0) {
      this.hidden = true;
      return;
    }
    const template = this.dataset.labelSlide;
    for (const [index, slide] of remaining.entries()) {
      if (template) slide.setAttribute('aria-label', template.replace('[position]', String(index + 1)).replace('[total]', String(remaining.length)).replace('[title]', slide.dataset.slideTitle || ''));
    }
    for (const img of remaining[0].querySelectorAll('img')) {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
    }
  }

  #buildPagination(signal) {
    if (!this.pagination) return;
    this.pagination.replaceChildren();
    this.#dots = [];
    this.#counter = null;
    this.pagination.hidden = this.slides.length < 2;
    if (this.slides.length < 2) return;

    if (this.pagination.classList.contains('mc-hero-carousel__pagination--counter')) {
      this.#counter = document.createElement('p');
      this.#counter.className = 'mc-hero-carousel__counter';
      this.pagination.append(this.#counter);
      return;
    }

    const template = this.dataset.labelGoto || '[position]';
    for (const [index] of this.slides.entries()) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'mc-hero-carousel__dot';
      dot.setAttribute('aria-label', template.replace('[position]', String(index + 1)));
      dot.setAttribute('aria-controls', this.track.id);
      dot.addEventListener('click', () => {
        this.#userInteracted = true;
        this.#stopAutoplay(true);
        this.#scrollToIndex(index);
      }, { signal });
      this.#dots.push(dot);
    }
    this.pagination.append(...this.#dots);
  }

  #clearTimers() {
    cancelAnimationFrame(this.#animationFrame);
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#programmaticTimer);
    this.#clearAutoplayTimer();
    this.#animationFrame = 0;
    this.#settleTimer = 0;
    this.#programmaticTimer = 0;
  }

  #setupLoop() {
    if (this.#loop || this.dataset.loop !== 'true' || this.slides.length < 2 || this.track.getBoundingClientRect().width <= 0) return;
    const before = this.slides.map((slide, index) => this.#copySlide(slide, index));
    const after = this.slides.map((slide, index) => this.#copySlide(slide, index));
    this.track.prepend(...before);
    this.track.append(...after);
    this.#items = [...before, ...this.slides, ...after];
    this.#loop = true;
    this.classList.add('is-looping');
    this.#scrollToPhysical(this.slides.length + this.#activeIndex, true);
  }

  #onResize() {
    this.#setupLoop();
    if (this.#loop) {
      this.#scrollToPhysical(this.slides.length + this.#activeIndex, true);
      return;
    }
    this.#requestMetadataUpdate();
  }

  #copySlide(slide, index) {
    const copy = slide.cloneNode(true);
    for (const element of [copy, ...copy.querySelectorAll('*')]) {
      for (const attribute of [...element.attributes]) {
        if (attribute.name === 'id' || attribute.name === 'data-block-id' || attribute.name.startsWith('data-shopify-') || ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].includes(attribute.name)) element.removeAttribute(attribute.name);
      }
    }
    // Visual buffers must not repeat the page headline in the document outline.
    for (const heading of copy.querySelectorAll('h1, h2, h3, h4, h5, h6')) {
      const replacement = document.createElement('div');
      for (const attribute of [...heading.attributes]) replacement.setAttribute(attribute.name, attribute.value);
      replacement.append(...heading.childNodes);
      heading.replaceWith(replacement);
    }
    // Belt and braces for browsers without `inert`.
    for (const focusable of copy.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      focusable.setAttribute('tabindex', '-1');
    }
    copy.removeAttribute('data-mc-hero-slide');
    copy.removeAttribute('data-mc-start');
    copy.removeAttribute('data-mc-end');
    copy.dataset.mcHeroCopy = String(index);
    copy.setAttribute('aria-hidden', 'true');
    copy.inert = true;
    for (const img of copy.querySelectorAll('img')) {
      img.setAttribute('fetchpriority', 'low');
      img.loading = 'lazy';
    }
    return copy;
  }

  #logicalIndex(physical) {
    return this.#loop ? ((physical - this.slides.length) % this.slides.length + this.slides.length) % this.slides.length : physical;
  }

  #nearestPhysical() {
    if (!this.track || this.#items.length === 0) return 0;
    const trackBox = this.track.getBoundingClientRect();
    const trackCenter = trackBox.left + trackBox.width / 2;
    let nearest = 0;
    let distance = Number.POSITIVE_INFINITY;

    for (const [index, slide] of this.#items.entries()) {
      const box = slide.getBoundingClientRect();
      const nextDistance = Math.abs(box.left + box.width / 2 - trackCenter);
      if (nextDistance < distance) {
        distance = nextDistance;
        nearest = index;
      }
    }

    return nearest;
  }

  #nearestIndex() {
    return this.#logicalIndex(this.#nearestPhysical());
  }

  #requestMetadataUpdate() {
    if (this.#animationFrame) return;
    this.#animationFrame = requestAnimationFrame(() => {
      this.#animationFrame = 0;
      this.#applyState(this.#nearestIndex(), false);
    });
  }

  #applyState(index, announce) {
    const nextIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const focused = document.activeElement;
    const focusedSlide = this.slides.find((slide) => slide.contains(focused));

    if (focusedSlide && focusedSlide !== this.slides[nextIndex]) {
      this.track.focus({ preventScroll: true });
    }

    this.#activeIndex = nextIndex;
    if (this.#loop) {
      for (const offset of [-1, 0, 1]) {
        const nearby = (nextIndex + offset + this.slides.length) % this.slides.length;
        for (const img of this.slides[nearby].querySelectorAll('img')) img.loading = 'eager';
      }
    }
    for (const [slideIndex, slide] of this.slides.entries()) {
      const active = slideIndex === nextIndex;
      slide.setAttribute('aria-hidden', String(!active));
      slide.inert = !active;
    }

    const controlIndex = this.#programmatic ? this.#requestedIndex : nextIndex;
    if (!this.#loop && ((focused === this.previousButton && controlIndex === 0) || (focused === this.nextButton && controlIndex === this.slides.length - 1))) {
      this.track.focus({ preventScroll: true });
    }
    if (this.previousButton) this.previousButton.disabled = !this.#loop && controlIndex === 0;
    if (this.nextButton) this.nextButton.disabled = !this.#loop && controlIndex === this.slides.length - 1;
    for (const [dotIndex, dot] of this.#dots.entries()) {
      if (dotIndex === controlIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
    if (this.#counter) this.#counter.textContent = `${controlIndex + 1} / ${this.slides.length}`;
    if (announce && this.#userInteracted && this.status) this.status.textContent = this.slides[nextIndex]?.getAttribute('aria-label') || '';
  }

  #onScroll() {
    this.#requestMetadataUpdate();
    clearTimeout(this.#settleTimer);
    if (!this.#supportsScrollEnd) this.#settleTimer = setTimeout(() => this.#settle(true), 250);
  }

  #settle(announce) {
    clearTimeout(this.#settleTimer);
    clearTimeout(this.#programmaticTimer);
    this.#settleTimer = 0;
    this.#programmaticTimer = 0;
    const physical = this.#nearestPhysical();
    const logical = this.#logicalIndex(physical);
    if (this.#loop && this.#items[physical]?.hasAttribute('data-mc-hero-copy')) {
      this.#scrollToPhysical(this.slides.length + logical, true);
      return;
    }
    this.#programmatic = false;
    const nearest = logical;
    this.#requestedPhysical = physical;
    this.#requestedIndex = nearest;
    this.#applyState(nearest, announce);
    this.#scheduleAutoplay();
  }

  #moveBy(amount) {
    this.#userInteracted = true;
    this.#stopAutoplay(true);
    this.#advance(amount);
  }

  #advance(amount) {
    if (this.#loop) {
      let origin = this.#programmatic ? this.#requestedPhysical : this.#nearestPhysical();
      if (origin + amount < 0 || origin + amount >= this.#items.length) {
        origin = this.slides.length + this.#logicalIndex(origin);
        this.#scrollToPhysical(origin, true);
      }
      this.#scrollToPhysical(origin + amount);
      return;
    }
    const origin = this.#programmatic ? this.#requestedIndex : this.#nearestIndex();
    const target = Math.max(0, Math.min(origin + amount, this.slides.length - 1));
    this.#scrollToIndex(target);
  }

  #scrollToIndex(index, instant = false) {
    const target = Math.max(0, Math.min(index, this.slides.length - 1));
    this.#scrollToPhysical(target + (this.#loop ? this.slides.length : 0), instant);
  }

  #scrollToPhysical(physical, instant = false) {
    const slide = this.#items[physical];
    if (!slide || !this.track) return;

    const target = this.#logicalIndex(physical);
    this.#requestedPhysical = physical;
    this.#requestedIndex = target;
    this.#programmatic = true;
    const trackBox = this.track.getBoundingClientRect();
    const slideBox = slide.getBoundingClientRect();
    const delta = slideBox.left + slideBox.width / 2 - (trackBox.left + trackBox.width / 2);
    const reduced = this.#motionQuery?.matches;
    this.track.scrollBy({ left: delta, top: 0, behavior: instant || reduced ? 'auto' : 'smooth' });
    this.#applyState(target, false);
    clearTimeout(this.#programmaticTimer);
    if (Math.abs(delta) < 0.5) this.#settle(true);
    else if (!this.#supportsScrollEnd) this.#programmaticTimer = setTimeout(() => this.#settle(true), reduced || instant ? 40 : 700);
  }

  #isEditable(target) {
    return target instanceof Element && Boolean(target.closest('input, textarea, select, [contenteditable="true"]'));
  }

  #onKeydown(event) {
    if (this.#isEditable(event.target)) return;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    let target = null;

    if (event.key === 'Home') target = 0;
    if (event.key === 'End') target = this.slides.length - 1;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#moveBy((event.key === 'ArrowRight' ? 1 : -1) * (rtl ? -1 : 1));
      return;
    }
    if (target === null) return;

    event.preventDefault();
    this.#userInteracted = true;
    this.#stopAutoplay(true);
    this.#scrollToIndex(target);
  }

  // Neighboring cards are inert, so a click on a peek lands on the track itself.
  #onTrackClick(event) {
    if (event.target !== this.track || this.slides.length < 2) return;
    const current = this.#items[this.#programmatic ? this.#requestedPhysical : this.#nearestPhysical()];
    if (!current) return;
    const box = current.getBoundingClientRect();
    if (event.clientX >= box.left && event.clientX <= box.right) return;
    const rtl = getComputedStyle(this.track).direction === 'rtl';
    this.#moveBy((event.clientX > box.right ? 1 : -1) * (rtl ? -1 : 1));
  }

  #onBlockSelect(event) {
    const blockId = event.detail?.blockId || event.target?.closest?.('[data-block-id]')?.dataset.blockId;
    const index = this.slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index < 0) return;
    this.#stopAutoplay(true);
    this.#scrollToIndex(index, true);
  }

  #autoplayEnabled() {
    return this.dataset.autoplay === 'true' && this.dataset.editor !== 'true' && this.slides.length > 1 && !this.#motionQuery?.matches;
  }

  #clearAutoplayTimer() {
    clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = 0;
    for (const dot of this.#dots) dot.classList.remove('is-progressing');
  }

  #scheduleAutoplay() {
    this.#clearAutoplayTimer();
    if (!this.#autoplayEnabled() || this.#userStopped || this.#hovered || document.hidden || this.#programmatic) return;
    if (!this.#loop && this.#activeIndex >= this.slides.length - 1) {
      this.#userStopped = true;
      this.#syncPlayButton();
      return;
    }

    const delay = Math.max(5000, Number.parseInt(this.dataset.autoplayDelay, 10) || 8000);
    const dot = this.#dots[this.#activeIndex];
    if (dot) {
      this.style.setProperty('--mc-hero-autoplay-duration', `${delay}ms`);
      void dot.offsetWidth;
      dot.classList.add('is-progressing');
    }
    this.#autoplayTimer = setTimeout(() => {
      this.#autoplayTimer = 0;
      this.#advance(1);
    }, delay);
  }

  #stopAutoplay(persistent) {
    this.#clearAutoplayTimer();
    if (persistent && this.dataset.autoplay === 'true') this.#userStopped = true;
    this.#syncPlayButton();
  }

  #onManualInput() {
    this.#userInteracted = true;
    this.#stopAutoplay(true);
    this.#programmatic = false;
    this.#requestedIndex = this.#nearestIndex();
    this.#requestedPhysical = this.#nearestPhysical();
    clearTimeout(this.#programmaticTimer);
    this.#programmaticTimer = 0;
  }

  #toggleAutoplay() {
    if (!this.#autoplayEnabled()) return;
    const shouldPlay = this.#playIntent ?? this.#userStopped;
    this.#playIntent = null;
    if (shouldPlay) {
      if (!this.#loop && this.#activeIndex >= this.slides.length - 1) this.#scrollToIndex(0, true);
      this.#userStopped = false;
      this.#syncPlayButton();
      this.#scheduleAutoplay();
      return;
    }
    this.#stopAutoplay(true);
  }

  #syncPlayButton() {
    const playing = this.#autoplayEnabled() && !this.#userStopped;
    if (this.status) this.status.setAttribute('aria-live', playing ? 'off' : 'polite');
    if (!this.playButton) return;
    this.playButton.disabled = Boolean(this.#motionQuery?.matches);
    this.playButton.setAttribute('aria-label', playing ? this.dataset.labelPause : this.dataset.labelPlay);
    const icon = this.playButton.querySelector('[data-mc-hero-play-icon]');
    if (icon) icon.textContent = playing ? 'Ⅱ' : '▶';
  }

  #setHovered(hovered) {
    this.#hovered = hovered;
    if (hovered) {
      this.#clearAutoplayTimer();
      return;
    }
    this.#scheduleAutoplay();
  }

  #onVisibilityChange() {
    if (document.hidden) {
      this.#clearAutoplayTimer();
      return;
    }
    this.#scheduleAutoplay();
  }

  #onMotionChange() {
    if (this.#motionQuery?.matches) this.#stopAutoplay(false);
    this.#syncPlayButton();
    this.#scheduleAutoplay();
  }
}

customElements.define('mc-hero-carousel', MCHeroCarousel);
}
